/**
 * Delba Polygons — Main entrypoint (Plan 02: full canvas editor wiring)
 *
 * Wires together:
 *   - Bounding box modal (startup + re-open from toolbar)
 *   - Canvas renderer (initCanvas + render)
 *   - Viewport zoom/pan (scroll-wheel, toolbar dropdown, fit-to-view)
 *   - Status bar (cursor lat/lng + zoom %)
 *   - Space+drag and middle-click panning
 *   - Draw mode: vertex placement, rubber-band preview, polygon closing
 *   - Mode switching: select/draw via toolbar buttons and V/D keys
 */

import { pixelToLatLng, latLngToPixel, edgeLengthM } from "../../javascript/canvas/projection.js";
import { initCanvas, render, getLogicalSize, setDrawingState, setHoverState, setSelectedVertexState } from "../../javascript/canvas/renderer.js";
import { zoomAtPoint, setZoom, fitToView, getViewport, pan } from "../../javascript/canvas/viewport.js";
import { openBboxModal } from "../../javascript/ui/bboxModal.js";
import { initToolbar, syncZoomDropdown, syncModeButtons, initColorPicker, syncColorButton, initUnderlayControls, syncUnderlayControls } from "../../javascript/ui/toolbar.js";
import { updateCursor, updateZoom, updateColor, showTemporaryMessage, updateEdgeLength } from "../../javascript/ui/statusBar.js";
import { getBrushColor, setBrushColor, getBrushFillStyle, hexToFillStyle, getPalette } from "../../javascript/ui/brushColor.js";
import { addPolygon, isNearFirstVertex, getPolygons, setSelectedId, getSelectedId, deleteSelected, movePolygon, pointInPolygon, clampDelta, canvasDeltaToLatLng, clearPolygons, setPolygons, moveToFront, sendToBack, bringForward, sendBackward, moveVertex, insertVertex, deleteVertex, polygonIsSelfIntersecting, pointToSegmentDistance, closestEdge } from "../../javascript/canvas/polygons.js";
import { initMenuBar, syncEditArrangeMenus, syncFileMenu } from "../../javascript/ui/menuBar.js";
import { pushSnapshot, undo, redo, clearHistory, canUndo, canRedo } from "../../javascript/canvas/history.js";
import { saveAs, openFile, openUnderlayFile, validateDelb, confirmUnsavedChanges, markDirty, clearDirty, isDirty } from "../../javascript/canvas/fileIO.js";
import { setUnderlay, clearUnderlay, getUnderlayFilename, getUnderlayDataUrl, getUnderlayScale, setUnderlayScale, hasUnderlay as hasUnderlayLoaded, isUnderlayVisible, setUnderlayVisible } from "../../javascript/canvas/underlay.js";

document.addEventListener("DOMContentLoaded", () => {
  const canvas       = document.getElementById("main-canvas");
  const placeholder  = document.getElementById("canvas-placeholder");

  /** The most recently confirmed bounding box. */
  let currentBbox = null;

  // ─────────────────────────────────────────────
  // Mode state
  // ─────────────────────────────────────────────
  let activeMode        = 'select'; // 'select' | 'draw'
  let drawingVertices   = [];       // lat/lng vertices being placed
  let drawingCursorPos  = null;     // { canvasX, canvasY } for rubber-band

  // ─────────────────────────────────────────────
  // Select mode drag state
  // ─────────────────────────────────────────────
  let isDraggingPolygon = false;
  let dragStartCanvas   = null;     // { canvasX, canvasY } at mousedown
  let suppressClick     = false;    // prevent click from firing after drag

  // ─────────────────────────────────────────────
  // Vertex drag state
  // ─────────────────────────────────────────────
  let isDraggingVertex      = false;
  let draggingVertexIndex   = -1;
  let lastValidVertices     = null;  // deep copy of vertices at last valid drag position
  let dragStartCanvasVertex = null;  // { canvasX, canvasY } reference point for delta

  // ─────────────────────────────────────────────
  // Vertex selection state
  // ─────────────────────────────────────────────
  let selectedVertexIndex   = null;  // index of selected vertex, or null

  // ─────────────────────────────────────────────
  // Deferred snapshot flag (push on first move, not mousedown)
  // ─────────────────────────────────────────────
  let dragSnapshotPushed    = false;

  // ─────────────────────────────────────────────
  // Pan / drag state (outer scope so setMode can read them)
  // ─────────────────────────────────────────────
  let isPanMode   = false; // space key held
  let isDragging  = false;
  let didPanMove  = false; // true once mousemove fires during a pan drag

  // ─────────────────────────────────────────────
  // Undo/redo helpers
  // ─────────────────────────────────────────────

  function captureSnapshot() {
    return {
      polygons: getPolygons().map(p => ({
        id: p.id,
        vertices: p.vertices.map(v => ({ ...v })),
        fillColor: p.fillColor,
        fillColorHex: p.fillColorHex,
        zIndex: p.zIndex ?? 0,
      })),
      selectedId: getSelectedId(),
    };
  }

  function restoreSnapshot(snapshot) {
    setPolygons(snapshot.polygons);
    setSelectedId(snapshot.selectedId);
  }

  function syncMenuState() {
    syncEditArrangeMenus({
      canUndo: canUndo(),
      canRedo: canRedo(),
      hasSelection: !!getSelectedId(),
    });
  }

  function handleUndo() {
    const snapshot = undo(captureSnapshot());
    if (!snapshot) return;
    restoreSnapshot(snapshot);
    selectedVertexIndex = null;
    setSelectedVertexState({ index: null, polygonId: null });
    markDirty();
    render();
    syncMenuState();
  }

  function handleRedo() {
    const snapshot = redo(captureSnapshot());
    if (!snapshot) return;
    restoreSnapshot(snapshot);
    selectedVertexIndex = null;
    setSelectedVertexState({ index: null, polygonId: null });
    markDirty();
    render();
    syncMenuState();
  }

  /**
   * Show the styled delete confirmation dialog.
   * Returns a Promise that resolves true (confirm) or false (cancel/close).
   */
  function confirmDelete() {
    const deleteDialog = document.getElementById('delete-confirm-dialog');
    return new Promise((resolve) => {
      const onConfirm = () => { cleanup(); resolve(true); };
      const onCancel  = () => { cleanup(); resolve(false); };
      const onClose   = () => { cleanup(); resolve(false); };
      function cleanup() {
        document.getElementById('delete-confirm').removeEventListener('click', onConfirm);
        document.getElementById('delete-cancel').removeEventListener('click', onCancel);
        deleteDialog.removeEventListener('close', onClose);
        deleteDialog.close();
      }
      document.getElementById('delete-confirm').addEventListener('click', onConfirm);
      document.getElementById('delete-cancel').addEventListener('click', onCancel);
      deleteDialog.addEventListener('close', onClose);
      deleteDialog.showModal();
    });
  }

  function handleDelete() {
    if (activeMode !== 'select' || !getSelectedId()) return;

    if (selectedVertexIndex !== null) {
      const selectedPoly = getPolygons().find(p => p.id === getSelectedId());
      if (!selectedPoly) return;

      if (selectedPoly.vertices.length <= 3) {
        showTemporaryMessage("Cannot delete: polygon needs at least 3 vertices", 3000);
        return;
      }

      const logicalSize = getLogicalSize();
      const candidateVerts = selectedPoly.vertices.filter((_, i) => i !== selectedVertexIndex);
      const candidatePixelVerts = candidateVerts.map(v => latLngToPixel(v.lat, v.lng, currentBbox, logicalSize));
      if (polygonIsSelfIntersecting(candidatePixelVerts)) {
        showTemporaryMessage("Cannot delete: polygon would self-intersect", 3000);
        return;
      }

      pushSnapshot(captureSnapshot());
      deleteVertex(getSelectedId(), selectedVertexIndex);
      selectedVertexIndex = null;
      setSelectedVertexState({ index: null, polygonId: null });
      render();
      markDirty();
      syncMenuState();
    } else {
      confirmDelete().then((confirmed) => {
        if (confirmed) {
          pushSnapshot(captureSnapshot());
          deleteSelected();
          selectedVertexIndex = null;
          setSelectedVertexState({ index: null, polygonId: null });
          render();
          markDirty();
          syncMenuState();
        }
      });
    }
  }

  // ─────────────────────────────────────────────
  // Toolbar initialisation
  // ─────────────────────────────────────────────
  initToolbar({
    onBboxOpen: () => {
      openBboxModal(
        (bbox) => _onBboxConfirmed(bbox),
        currentBbox
      );
    },
    onZoomChange: (newScale) => {
      const { width, height } = getLogicalSize();
      setZoom(newScale, width, height);
      render();
      updateZoom(getViewport().scale);
    },
    onFitToView: () => {
      fitToView();
      render();
      syncZoomDropdown(getViewport().scale);
      updateZoom(getViewport().scale);
    },
    onModeChange: (mode) => setMode(mode),
  });

  // ─────────────────────────────────────────────
  // Underlay toolbar controls initialisation
  // ─────────────────────────────────────────────
  initUnderlayControls({
    onToggle: () => {
      setUnderlayVisible(!isUnderlayVisible());
      syncUnderlayControls(hasUnderlayLoaded(), isUnderlayVisible(), getUnderlayScale());
      render();
    },
    onScaleChange: (newScale) => {
      setUnderlayScale(newScale);
      syncUnderlayControls(hasUnderlayLoaded(), isUnderlayVisible(), getUnderlayScale());
      render();
      markDirty();
    },
  });

  // ─────────────────────────────────────────────
  // File operations
  // ─────────────────────────────────────────────

  function handleSaveAs() {
    saveAs(currentBbox, getPolygons(), getViewport(), getUnderlayDataUrl(), getUnderlayScale(), getUnderlayFilename());
    clearDirty();
  }

  function handleOpenUnderlay() {
    openUnderlayFile((img, filename) => {
      setUnderlay(img, filename);
      syncUnderlayControls(hasUnderlayLoaded(), isUnderlayVisible(), getUnderlayScale());
      syncFileMenu({ hasUnderlay: hasUnderlayLoaded() });
      render();
      markDirty();
    });
  }

  function handleRemoveUnderlay() {
    clearUnderlay();
    syncUnderlayControls(false, true, 1.0);
    syncFileMenu({ hasUnderlay: false });
    markDirty();
    render();
  }

  async function handleNew() {
    if (isDirty()) {
      const result = await confirmUnsavedChanges();
      if (result === "cancel") {
        render();
        return;
      }
      if (result === "save") handleSaveAs();
    }
    clearPolygons();
    clearHistory();
    clearUnderlay();
    syncUnderlayControls(false, true, 1.0);
    syncFileMenu({ hasUnderlay: false });
    fitToView();
    clearDirty();
    syncMenuState();
    currentBbox = null;
    if (canvas) canvas.style.display = "none";
    if (placeholder) placeholder.style.display = "flex";
    render();
    openBboxModal((bbox) => _onBboxConfirmed(bbox), null);
  }

  async function handleOpen() {
    if (isDirty()) {
      const result = await confirmUnsavedChanges();
      if (result === "cancel") {
        render();
        return;
      }
      if (result === "save") handleSaveAs();
    }
    openFile((data) => {
      const errors = validateDelb(data);
      if (errors.length > 0) {
        window.alert("Invalid .delb file: " + errors.join(", "));
        return;
      }
      if (data.header.version > 1) {
        console.warn("This file was created with a newer version of Delba. Some features may not display correctly.");
      }
      clearPolygons();
      setPolygons(data.polygons);
      clearHistory();
      currentBbox = data.header.boundingBox;
      if (placeholder) placeholder.style.display = "none";
      if (canvas) canvas.style.display = "block";
      fitToView();
      initCanvas(canvas, currentBbox);
      const logicalSize = getLogicalSize();
      setZoom(data.header.zoom, logicalSize.width, logicalSize.height);
      // Restore underlay from embedded data URL in .delb file
      clearUnderlay();
      if (data.underlay && data.underlay.dataUrl) {
        const img = new Image();
        img.onload = () => {
          setUnderlayScale(data.underlay.scale);
          setUnderlay(img, data.underlay.filename || "underlay.png", data.underlay.dataUrl);
          syncUnderlayControls(hasUnderlayLoaded(), isUnderlayVisible(), getUnderlayScale());
          syncFileMenu({ hasUnderlay: hasUnderlayLoaded() });
          render();
        };
        img.src = data.underlay.dataUrl;
      }
      syncUnderlayControls(hasUnderlayLoaded(), isUnderlayVisible(), getUnderlayScale());
      syncFileMenu({ hasUnderlay: hasUnderlayLoaded() });
      render();
      syncZoomDropdown(getViewport().scale);
      updateZoom(getViewport().scale);
      setSelectedId(null);
      clearDirty();
      syncMenuState();
      setMode('select');
    });
  }

  // ─────────────────────────────────────────────
  // Menu bar initialisation
  // ─────────────────────────────────────────────
  initMenuBar({
    onNew: () => handleNew(),
    onOpen: () => handleOpen(),
    onSaveAs: () => handleSaveAs(),
    onOpenUnderlay: () => handleOpenUnderlay(),
    onRemoveUnderlay: () => handleRemoveUnderlay(),
    onUndo: handleUndo,
    onRedo: handleRedo,
    onBringToFront: () => { pushSnapshot(captureSnapshot()); moveToFront(getSelectedId()); markDirty(); render(); syncMenuState(); },
    onBringForward: () => { pushSnapshot(captureSnapshot()); bringForward(getSelectedId()); markDirty(); render(); syncMenuState(); },
    onSendBackward: () => { pushSnapshot(captureSnapshot()); sendBackward(getSelectedId()); markDirty(); render(); syncMenuState(); },
    onSendToBack:   () => { pushSnapshot(captureSnapshot()); sendToBack(getSelectedId()); markDirty(); render(); syncMenuState(); },
    onDelete: handleDelete,
  });
  syncMenuState(); // Set initial disabled states

  // ─────────────────────────────────────────────
  // Color picker initialisation
  // ─────────────────────────────────────────────
  initColorPicker(getPalette(), (name, hex) => {
    setBrushColor(name, hex);
    syncColorButton(hex);
    updateColor(name, hex);

    // If a polygon is selected, update its color too
    const selId = getSelectedId();
    if (selId) {
      const poly = getPolygons().find(p => p.id === selId);
      if (poly) {
        pushSnapshot(captureSnapshot());
        poly.fillColor = hexToFillStyle(hex);
        poly.fillColorHex = hex;
        render();
        markDirty();
        syncMenuState();
      }
    }
  });

  // ─────────────────────────────────────────────
  // Internal: handle confirmed bbox
  // ─────────────────────────────────────────────
  function _onBboxConfirmed(bbox) {
    currentBbox = bbox;

    // Swap placeholder → canvas
    if (placeholder) placeholder.style.display = "none";
    if (canvas) canvas.style.display = "block";

    // Reset viewport to 100% each time bbox changes
    fitToView();

    initCanvas(canvas, bbox);
    render();
    markDirty();

    updateZoom(getViewport().scale);
    syncZoomDropdown(getViewport().scale);
    syncUnderlayControls(hasUnderlayLoaded(), isUnderlayVisible(), getUnderlayScale());
    syncFileMenu({ hasUnderlay: hasUnderlayLoaded() });

    // Set initial mode
    setMode('select');
  }

  // ─────────────────────────────────────────────
  // Mode switching
  // ─────────────────────────────────────────────
  function setMode(mode) {
    activeMode = mode;
    // Clear vertex selection on mode change
    selectedVertexIndex = null;
    setSelectedVertexState({ index: null, polygonId: null });
    // Cancel any in-progress drawing when switching away from draw
    if (mode !== 'draw') {
      drawingVertices = [];
      drawingCursorPos = null;
      setDrawingState(null);
    }
    // Update cursor (only if not in pan mode)
    if (!isPanMode && !isDragging) {
      canvas.style.cursor = mode === 'draw' ? 'crosshair' : 'default';
    }
    syncModeButtons(mode);
    render();
  }

  // ─────────────────────────────────────────────
  // Draw mode helpers
  // ─────────────────────────────────────────────

  /**
   * Extract canvas-space coordinates from a mouse event.
   * Inverts the viewport transform.
   */
  function cssToCanvasCoords(e) {
    const rect = canvas.getBoundingClientRect();
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;
    const { scale, offsetX, offsetY } = getViewport();
    return {
      canvasX: (cssX - offsetX) / scale,
      canvasY: (cssY - offsetY) / scale,
    };
  }

  /**
   * Finalize the current in-progress drawing: add polygon, reset draw state.
   */
  function finalizeDrawing() {
    pushSnapshot(captureSnapshot());
    const { hex } = getBrushColor();
    addPolygon([...drawingVertices], hexToFillStyle(hex), hex);
    drawingVertices = [];
    drawingCursorPos = null;
    setDrawingState(null);
    render();
    markDirty();
    syncMenuState();
  }

  /**
   * Cancel the current in-progress drawing without adding a polygon.
   */
  function cancelDrawing() {
    drawingVertices = [];
    drawingCursorPos = null;
    setDrawingState(null);
    render();
  }

  /**
   * Handle a canvas click in draw mode.
   */
  function handleDrawClick(e) {
    if (!currentBbox) return;

    const { canvasX, canvasY } = cssToCanvasCoords(e);
    const logicalSize = getLogicalSize();
    const { lat, lng } = pixelToLatLng(canvasX, canvasY, currentBbox, logicalSize);

    // Check if clicking near first vertex (close polygon gesture)
    if (drawingVertices.length >= 2) {
      const firstPixel = latLngToPixel(
        drawingVertices[0].lat,
        drawingVertices[0].lng,
        currentBbox,
        logicalSize
      );
      if (isNearFirstVertex(canvasX, canvasY, firstPixel, 10) && drawingVertices.length >= 3) {
        // Self-intersection check before finalizing
        const pixelVerts = drawingVertices.map(v => latLngToPixel(v.lat, v.lng, currentBbox, logicalSize));
        if (polygonIsSelfIntersecting(pixelVerts)) {
          drawingVertices = [];
          drawingCursorPos = null;
          setDrawingState(null);
          render();
          showTemporaryMessage("Polygon cancelled: closing edge would cross", 3000);
          return;
        }
        finalizeDrawing();
        return;
      }
    }

    // Place a new vertex
    drawingVertices.push({ lat, lng });
    setDrawingState({ vertices: [...drawingVertices], cursorPos: drawingCursorPos });
    render();
  }

  /**
   * Handle a canvas click in select mode.
   * Iterates polygons in reverse (topmost first), selects first hit.
   */
  function handleSelectClick(e) {
    if (!currentBbox) return;

    // Clear vertex selection on any select-mode click
    selectedVertexIndex = null;
    setSelectedVertexState({ index: null, polygonId: null });

    const { canvasX, canvasY } = cssToCanvasCoords(e);
    const logicalSize = getLogicalSize();

    // Sort by descending zIndex: topmost (highest zIndex) is checked first
    const sorted = [...getPolygons()].sort((a, b) => (b.zIndex ?? 0) - (a.zIndex ?? 0));

    for (const polygon of sorted) {
      const pixelVerts = polygon.vertices.map(v =>
        latLngToPixel(v.lat, v.lng, currentBbox, logicalSize)
      );
      if (pointInPolygon(canvasX, canvasY, pixelVerts)) {
        setSelectedId(polygon.id);

        // Eyedropper: sync brush color to selected polygon's color
        const paletteEntry = getPalette().find(p => p.hex === polygon.fillColorHex);
        if (paletteEntry) {
          setBrushColor(paletteEntry.name, paletteEntry.hex);
          syncColorButton(paletteEntry.hex);
          updateColor(paletteEntry.name, paletteEntry.hex);
        }

        render();
        syncMenuState();
        return;
      }
    }

    // No polygon hit — deselect
    setSelectedId(null);
    render();
    syncMenuState();
  }

  // ─────────────────────────────────────────────
  // Scroll-wheel zoom
  // ─────────────────────────────────────────────
  if (canvas) {
    canvas.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();

        const rect    = canvas.getBoundingClientRect();
        const cursorX = e.clientX - rect.left;
        const cursorY = e.clientY - rect.top;
        const delta   = e.deltaY < 0 ? 0.1 : -0.1;

        zoomAtPoint(cursorX, cursorY, delta);
        render();
        syncZoomDropdown(getViewport().scale);
        updateZoom(getViewport().scale);
      },
      { passive: false }
    );

    // ─────────────────────────────────────────────
    // Cursor coordinate readout (mousemove)
    // ─────────────────────────────────────────────
    canvas.addEventListener("mousemove", (e) => {
      if (!currentBbox) return;
      if (isDragging) return; // freeze coordinates during pan drag

      const rect    = canvas.getBoundingClientRect();
      const cssX    = e.clientX - rect.left;
      const cssY    = e.clientY - rect.top;

      // Invert viewport transform to get canvas-space coordinates
      const { scale, offsetX, offsetY } = getViewport();
      const canvasX = (cssX - offsetX) / scale;
      const canvasY = (cssY - offsetY) / scale;

      const logicalSize = getLogicalSize();
      const { lat, lng } = pixelToLatLng(canvasX, canvasY, currentBbox, logicalSize);
      updateCursor(lat, lng);

      // Vertex drag (must be checked BEFORE polygon body drag)
      if (isDraggingVertex && draggingVertexIndex !== -1 && dragStartCanvasVertex) {
        // Defer snapshot to first actual movement (fixes phantom undo entries)
        if (!dragSnapshotPushed) {
          pushSnapshot(captureSnapshot());
          dragSnapshotPushed = true;
        }
        const dx = canvasX - dragStartCanvasVertex.canvasX;
        const dy = canvasY - dragStartCanvasVertex.canvasY;
        const { dLat, dLng } = canvasDeltaToLatLng(dx, dy, currentBbox, logicalSize);

        const selectedPoly = getPolygons().find(p => p.id === getSelectedId());
        if (selectedPoly) {
          const v = selectedPoly.vertices[draggingVertexIndex];
          // Clamp candidate position to bounding box
          const candidateLat = Math.max(currentBbox.bottomLat, Math.min(currentBbox.topLat, v.lat + dLat));
          const candidateLng = Math.max(currentBbox.leftLng, Math.min(currentBbox.rightLng, v.lng + dLng));

          // Build candidate pixel vertices for self-intersection test
          const candidatePixelVerts = selectedPoly.vertices.map((vert, i) => {
            if (i === draggingVertexIndex) {
              return latLngToPixel(candidateLat, candidateLng, currentBbox, logicalSize);
            }
            return latLngToPixel(vert.lat, vert.lng, currentBbox, logicalSize);
          });

          if (!polygonIsSelfIntersecting(candidatePixelVerts)) {
            // Valid position — apply move
            moveVertex(getSelectedId(), draggingVertexIndex, candidateLat, candidateLng);
            lastValidVertices = selectedPoly.vertices.map(vert => ({ ...vert }));
            dragStartCanvasVertex = { canvasX, canvasY }; // Reset reference for continuous drag
          }
          // If invalid (self-intersecting): wall effect — keep last valid position, don't move
        }
        render();
        return;
      }

      // Polygon body drag in select mode
      if (isDraggingPolygon && dragStartCanvas) {
        // Defer snapshot to first actual movement (fixes phantom undo entries)
        if (!dragSnapshotPushed) {
          pushSnapshot(captureSnapshot());
          dragSnapshotPushed = true;
        }
        const dx = canvasX - dragStartCanvas.canvasX;
        const dy = canvasY - dragStartCanvas.canvasY;
        let { dLat, dLng } = canvasDeltaToLatLng(dx, dy, currentBbox, logicalSize);

        const selectedPoly = getPolygons().find(p => p.id === getSelectedId());
        if (selectedPoly) {
          const clamped = clampDelta(selectedPoly.vertices, dLat, dLng, currentBbox);
          dLat = clamped.dLat;
          dLng = clamped.dLng;
          movePolygon(getSelectedId(), dLat, dLng);
          dragStartCanvas = { canvasX, canvasY }; // Reset start for continuous drag
          render();
        }
        return;
      }

      // Rubber-band preview for draw mode
      if (activeMode === 'draw' && drawingVertices.length > 0) {
        drawingCursorPos = { canvasX, canvasY };

        // Check if cursor is near first vertex and closing would cause self-intersection
        let closingWouldIntersect = false;
        if (drawingVertices.length >= 3) {
          const firstPixel = latLngToPixel(drawingVertices[0].lat, drawingVertices[0].lng, currentBbox, logicalSize);
          if (isNearFirstVertex(canvasX, canvasY, firstPixel, 10)) {
            const pixelVerts = drawingVertices.map(v => latLngToPixel(v.lat, v.lng, currentBbox, logicalSize));
            closingWouldIntersect = polygonIsSelfIntersecting(pixelVerts);
          }
        }

        setDrawingState({ vertices: [...drawingVertices], cursorPos: drawingCursorPos, closingWouldIntersect });
        render();
      }

      // Cursor and hover state updates for select mode (vertex > edge > polygon body > default)
      if (activeMode === 'select' && !isPanMode && !isDragging && !isDraggingPolygon && !isDraggingVertex && getSelectedId()) {
        const selectedPoly = getPolygons().find(p => p.id === getSelectedId());
        if (selectedPoly) {
          const pixelVerts = selectedPoly.vertices.map(v =>
            latLngToPixel(v.lat, v.lng, currentBbox, logicalSize)
          );

          // Vertex proximity check (12px)
          let hitVertexIdx = -1;
          for (let i = 0; i < pixelVerts.length; i++) {
            if (Math.hypot(canvasX - pixelVerts[i].x, canvasY - pixelVerts[i].y) <= 12) {
              hitVertexIdx = i;
              break;
            }
          }

          if (hitVertexIdx !== -1) {
            // Hovering a vertex
            canvas.style.cursor = 'move';
            setHoverState({ vertexIndex: hitVertexIdx, polygonId: getSelectedId(), edgeHover: false });
            updateEdgeLength(null);
            render();
            return;
          }

          // Edge proximity check (8px)
          const edgeResult = closestEdge(canvasX, canvasY, pixelVerts);
          if (edgeResult.dist <= 8) {
            // Hovering near an edge — compute and display edge length
            canvas.style.cursor = 'crosshair';
            setHoverState({ vertexIndex: null, polygonId: null, edgeHover: true });
            const n = selectedPoly.vertices.length;
            const v1 = selectedPoly.vertices[edgeResult.edgeIndex];
            const v2 = selectedPoly.vertices[(edgeResult.edgeIndex + 1) % n];
            updateEdgeLength(edgeLengthM(v1, v2));
            render();
            return;
          }

          // Inside polygon body
          if (pointInPolygon(canvasX, canvasY, pixelVerts)) {
            canvas.style.cursor = 'move';
            setHoverState({ vertexIndex: null, polygonId: null, edgeHover: false });
            updateEdgeLength(null);
            render();
            return;
          }
        }

        // Default: not near any part of selected polygon
        canvas.style.cursor = 'grab';
        setHoverState({ vertexIndex: null, polygonId: null, edgeHover: false });
        updateEdgeLength(null);
        render();
        return;
      }

      // Select mode without selection — show grab for hover over any polygon
      if (activeMode === 'select' && !isPanMode && !isDragging && !isDraggingPolygon && !isDraggingVertex && !getSelectedId()) {
        const polygons = getPolygons();
        let hoveredId = null;
        for (let i = polygons.length - 1; i >= 0; i--) {
          const pxVerts = polygons[i].vertices.map(v =>
            latLngToPixel(v.lat, v.lng, currentBbox, logicalSize)
          );
          if (pointInPolygon(canvasX, canvasY, pxVerts)) {
            hoveredId = polygons[i].id;
            break;
          }
        }
        canvas.style.cursor = hoveredId ? 'grab' : 'default';
      }
    });

    canvas.addEventListener("mouseleave", () => {
      updateCursor(null, null);
      updateEdgeLength(null);
    });

    // ─────────────────────────────────────────────
    // Suppress browser context menu on canvas
    // ─────────────────────────────────────────────
    canvas.addEventListener("contextmenu", (e) => {
      e.preventDefault();
    });

    // ─────────────────────────────────────────────
    // Canvas click — draw mode vertex placement / select mode hit test
    // ─────────────────────────────────────────────
    canvas.addEventListener("click", (e) => {
      if (isPanMode || isDragging) return;
      // Suppress click if it followed a polygon drag or vertex drag
      if (suppressClick) { suppressClick = false; return; }
      if (activeMode === 'draw') handleDrawClick(e);
      else if (activeMode === 'select') handleSelectClick(e);
    });

    // ─────────────────────────────────────────────
    // Double-click — insert vertex on edge
    // ─────────────────────────────────────────────
    canvas.addEventListener("dblclick", (e) => {
      e.preventDefault(); // suppress text selection
      if (activeMode !== 'select' || !getSelectedId() || !currentBbox) return;

      const { canvasX, canvasY } = cssToCanvasCoords(e);
      const logicalSize = getLogicalSize();
      const selectedPoly = getPolygons().find(p => p.id === getSelectedId());
      if (!selectedPoly) return;

      const pixelVerts = selectedPoly.vertices.map(v =>
        latLngToPixel(v.lat, v.lng, currentBbox, logicalSize)
      );

      const result = closestEdge(canvasX, canvasY, pixelVerts);
      if (result.dist <= 8) {
        pushSnapshot(captureSnapshot());

        const n = selectedPoly.vertices.length;
        const v1 = selectedPoly.vertices[result.edgeIndex];
        const v2 = selectedPoly.vertices[(result.edgeIndex + 1) % n];
        const t = result.t;
        const newLat = v1.lat + t * (v2.lat - v1.lat);
        const newLng = v1.lng + t * (v2.lng - v1.lng);

        insertVertex(getSelectedId(), result.edgeIndex, { lat: newLat, lng: newLng });
        render();
        markDirty();
        syncMenuState();
      }

      // Set suppressClick to prevent the second synthetic click from dblclick deselecting
      suppressClick = true;
    });

    document.addEventListener("keydown", (e) => {
      // Undo: Ctrl+Z / Cmd+Z (without Shift)
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') {
        const tag = document.activeElement?.tagName;
        if (tag === "SELECT" || tag === "INPUT" || tag === "TEXTAREA" || tag === "BUTTON") return;
        if (isDraggingVertex) return; // guard: undo during vertex drag causes vertex index drift
        e.preventDefault();
        handleUndo();
        return;
      }

      // Redo: Ctrl+Shift+Z / Cmd+Shift+Z
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
        const tag = document.activeElement?.tagName;
        if (tag === "SELECT" || tag === "INPUT" || tag === "TEXTAREA" || tag === "BUTTON") return;
        if (isDraggingVertex) return; // guard: redo during vertex drag causes vertex index drift
        e.preventDefault();
        handleRedo();
        return;
      }

      if (e.code === "Space" && !e.repeat) {
        const tag = document.activeElement?.tagName;
        if (tag === "SELECT" || tag === "INPUT" || tag === "TEXTAREA" || tag === "BUTTON") return;
        e.preventDefault();
        isPanMode = true;
        canvas.style.cursor = "grab";
      }

      // Mode shortcuts (V = select, D = draw)
      if (e.key === "v" || e.key === "V") {
        const tag = document.activeElement?.tagName;
        if (tag === "SELECT" || tag === "INPUT" || tag === "TEXTAREA" || tag === "BUTTON") return;
        setMode('select');
      }
      if (e.key === "d" || e.key === "D") {
        const tag = document.activeElement?.tagName;
        if (tag === "SELECT" || tag === "INPUT" || tag === "TEXTAREA" || tag === "BUTTON") return;
        setMode('draw');
      }

      // Esc key — finish or cancel drawing; deselect in select mode
      if (e.key === "Escape" || e.code === "Escape") {
        if (activeMode === 'draw') {
          if (drawingVertices.length >= 3) {
            // Self-intersection check before finalizing via Esc
            const logicalSizeEsc = getLogicalSize();
            const pixelVertsEsc = drawingVertices.map(v => latLngToPixel(v.lat, v.lng, currentBbox, logicalSizeEsc));
            if (polygonIsSelfIntersecting(pixelVertsEsc)) {
              drawingVertices = [];
              drawingCursorPos = null;
              setDrawingState(null);
              render();
              showTemporaryMessage("Polygon cancelled: closing edge would cross", 3000);
            } else {
              finalizeDrawing();
            }
          } else {
            cancelDrawing();
          }
        } else if (activeMode === 'select' && getSelectedId()) {
          if (selectedVertexIndex !== null) {
            // First Esc: deselect vertex only
            selectedVertexIndex = null;
            setSelectedVertexState({ index: null, polygonId: null });
          } else {
            // Second Esc (no vertex selected): deselect polygon
            setSelectedId(null);
          }
          render();
          syncMenuState();
        }
      }

      // Delete/Backspace — delete selected vertex or polygon (select mode only)
      if ((e.key === 'Delete' || e.key === 'Backspace') && activeMode === 'select' && getSelectedId()) {
        const tag = document.activeElement?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        e.preventDefault();
        handleDelete();
      }
    });

    document.addEventListener("keyup", (e) => {
      if (e.code === "Space") {
        isPanMode = false;
        if (!isDragging) {
          canvas.style.cursor = activeMode === 'draw' ? 'crosshair' : 'default';
        }
      }
    });

    canvas.addEventListener("mousedown", (e) => {
      // Right-click in draw mode: finish or cancel
      if (e.button === 2 && activeMode === 'draw') {
        if (drawingVertices.length >= 3) {
          // Self-intersection check before finalizing via right-click
          const logicalSize = getLogicalSize();
          const pixelVerts = drawingVertices.map(v => latLngToPixel(v.lat, v.lng, currentBbox, logicalSize));
          if (polygonIsSelfIntersecting(pixelVerts)) {
            drawingVertices = [];
            drawingCursorPos = null;
            setDrawingState(null);
            render();
            showTemporaryMessage("Polygon cancelled: closing edge would cross", 3000);
          } else {
            finalizeDrawing();
          }
        } else {
          cancelDrawing();
        }
        return; // don't let pan logic fire
      }

      // Left click in select mode: check vertex hit first, then polygon drag
      if (e.button === 0 && activeMode === 'select' && !isPanMode && getSelectedId()) {
        const { canvasX, canvasY } = cssToCanvasCoords(e);
        const logicalSize = getLogicalSize();
        const selectedPoly = getPolygons().find(p => p.id === getSelectedId());
        if (selectedPoly) {
          const pixelVerts = selectedPoly.vertices.map(v =>
            latLngToPixel(v.lat, v.lng, currentBbox, logicalSize)
          );

          // Vertex hit test (12px radius) — must be checked BEFORE polygon body drag
          let hitIdx = -1;
          for (let i = 0; i < pixelVerts.length; i++) {
            if (Math.hypot(canvasX - pixelVerts[i].x, canvasY - pixelVerts[i].y) <= 12) {
              hitIdx = i;
              break;
            }
          }

          if (hitIdx !== -1) {
            // Start vertex drag (snapshot deferred to first mousemove)
            isDraggingVertex = true;
            draggingVertexIndex = hitIdx;
            dragSnapshotPushed = false;
            lastValidVertices = selectedPoly.vertices.map(v => ({ ...v }));
            dragStartCanvasVertex = { canvasX, canvasY };
            e.preventDefault();
            return; // Do NOT fall through to polygon drag
          }

          // Polygon body drag (snapshot deferred to first mousemove)
          if (pointInPolygon(canvasX, canvasY, pixelVerts)) {
            isDraggingPolygon = true;
            dragSnapshotPushed = false;
            dragStartCanvas = { canvasX, canvasY };
            e.preventDefault();
            return; // Don't trigger pan
          }
        }
      }

      // Left click in select mode on non-selected area: pan
      if (e.button === 0 && activeMode === 'select' && !isPanMode) {
        e.preventDefault();
        isDragging = true;
        didPanMove = false;
        canvas.style.cursor = 'grabbing';
      }

      // Left button while space held, or middle button
      if ((e.button === 0 && isPanMode) || e.button === 1) {
        e.preventDefault();
        isDragging = true;
        canvas.style.cursor = "grabbing";
      }
    });

    canvas.addEventListener("mousemove", (e) => {
      if (!isDragging) return;
      didPanMove = true;
      pan(e.movementX, e.movementY);
      render();
    });

    const _stopDrag = () => {
      if (isDraggingVertex) {
        if (!dragSnapshotPushed) {
          // No actual movement — click on vertex: select it
          selectedVertexIndex = draggingVertexIndex;
          setSelectedVertexState({ index: selectedVertexIndex, polygonId: getSelectedId() });
        } else {
          markDirty();
        }
        isDraggingVertex = false;
        draggingVertexIndex = -1;
        lastValidVertices = null;
        dragStartCanvasVertex = null;
        suppressClick = true;
        render();
        syncMenuState();
      }
      if (isDraggingPolygon) {
        if (!dragSnapshotPushed) {
          // No actual movement — click on body: deselect vertex
          selectedVertexIndex = null;
          setSelectedVertexState({ index: null, polygonId: null });
        } else {
          markDirty();
        }
        isDraggingPolygon = false;
        dragStartCanvas = null;
        suppressClick = true;
        canvas.style.cursor = 'move';
        render();
        syncMenuState();
      }
      if (isDragging) {
        isDragging = false;
        if (didPanMove) suppressClick = true;
        didPanMove = false;
        canvas.style.cursor = isPanMode ? "grab" : (activeMode === 'draw' ? 'crosshair' : 'grab');
      }
    };

    canvas.addEventListener("mouseup",    _stopDrag);
    canvas.addEventListener("mouseleave", _stopDrag);
  }
});
